import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Sun, Moon, Phone, Sparkles } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../ui/button';
import { EmeraldAIModal } from '../EmeraldAIModal';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default menu items in case API fails
const defaultNavLinks = [
  { name: 'Home', path: '/', order: 0 },
  { name: 'Rooms & Suites', path: '/rooms', order: 1 },
  { name: 'Dining', path: '/dining', order: 2 },
  { name: 'Gallery', path: '/gallery', order: 3 },
  { name: 'Explore', path: '/explore', order: 4 },
  { name: 'Tours & Travels', path: '/tours', order: 5 },
  { name: 'Contact', path: '/contact', order: 6 },
];

export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [menuLoading, setMenuLoading] = useState(true);
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  
  // Fetch dynamic menu items from API
  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/menu`);
        const apiItems = response.data.map(item => ({
          name: item.label,
          path: item.path,
          order: item.order,
          isExternal: item.is_external,
        }));
        setMenuItems(apiItems);
      } catch (err) {
        console.warn('Failed to fetch menu, using defaults:', err);
        setMenuItems(defaultNavLinks);
      } finally {
        setMenuLoading(false);
      }
    };
    
    fetchMenu();
  }, []);
  
  // Use fetched menu items or defaults, and always guarantee the Tours & Travels link
  const navLinks = useMemo(() => {
    const items = menuItems.length > 0 ? [...menuItems] : [...defaultNavLinks];
    if (!items.some((item) => item.path === '/tours')) {
      const contactIdx = items.findIndex((item) => item.path === '/contact');
      const toursLink = { name: 'Tours & Travels', path: '/tours', order: 5 };
      if (contactIdx >= 0) {
        items.splice(contactIdx, 0, toursLink);
      } else {
        items.push(toursLink);
      }
      return items;
    }
    return items.sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [menuItems]);
  
  // Pages with dark hero sections
  const isDarkHeroPage = ['/', '/book', '/rooms', '/dining', '/gallery', '/explore', '/events', '/contact', '/emerald-ai', '/offers', '/tours'].includes(location.pathname);
  
  // Dynamic text color based on scroll and page
  const getTextColor = () => {
    if (isScrolled || isMobileMenuOpen) {
      return 'text-emerald-900 dark:text-white';
    }
    return isDarkHeroPage ? 'text-white' : 'text-emerald-900 dark:text-white';
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled 
            ? 'bg-white/95 dark:bg-emerald-950/95 backdrop-blur-md shadow-lg' 
            : 'bg-transparent'
        }`}
        data-testid="main-navbar"
      >
        <nav className="container-custom">
          <div className="flex items-center justify-between h-20">
            
            {/* Logo - Left */}
            <Link 
              to="/" 
              className="flex items-center gap-3 flex-shrink-0"
              data-testid="logo-link"
            >
              <img 
                src="/images/logo.png" 
                alt="The Hotel Emerald" 
                className="h-12 w-auto"
              />
              <div className={`hidden sm:flex flex-col leading-tight ${getTextColor()}`}>
                <span className="font-serif text-lg tracking-wide">The Hotel</span>
                <span className="font-serif text-lg tracking-wide font-semibold">Emerald</span>
              </div>
            </Link>

            {/* Main Navigation - Center */}
            <div className="hidden lg:flex items-center gap-4 xl:gap-7">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`relative whitespace-nowrap text-xs xl:text-sm uppercase tracking-wide font-medium transition-colors duration-300 py-2 group ${getTextColor()} hover:text-emerald-600 dark:hover:text-emerald-400`}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                  {/* Underline animation */}
                  <span className={`absolute bottom-0 left-0 h-0.5 bg-gold transition-all duration-300 ${
                    isActive(link.path) ? 'w-full' : 'w-0 group-hover:w-full'
                  }`} />
                </Link>
              ))}
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center gap-3">
              
              {/* Emerald AI Button - Opens Modal */}
              <button
                onClick={() => setShowAIModal(true)}
                className={`hidden xl:flex items-center gap-1.5 px-4 py-2 text-xs uppercase tracking-wider font-medium rounded-full border transition-all duration-300 ${
                  isScrolled 
                    ? 'border-emerald-200 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/50' 
                    : isDarkHeroPage 
                      ? 'border-white/30 text-white hover:bg-white/10' 
                      : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'
                }`}
                data-testid="nav-emerald-ai-btn"
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span>Emerald AI</span>
              </button>

              {/* Divider */}
              <div className={`hidden xl:block w-px h-6 ${isScrolled ? 'bg-emerald-200 dark:bg-emerald-700' : isDarkHeroPage ? 'bg-white/30' : 'bg-emerald-200'}`} />

              {/* Theme Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className={`${getTextColor()} hover:bg-transparent`}
                data-testid="theme-toggle-btn"
              >
                {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
              </Button>

              {/* Book Now CTA */}
              <Link
                to="/book"
                className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-800 hover:bg-emerald-700 text-white text-sm uppercase tracking-wider font-medium transition-all duration-300 rounded-sm"
                data-testid="book-now-nav-btn"
              >
                <Phone className="h-4 w-4" />
                <span>Book Now</span>
              </Link>

              {/* Mobile Menu Toggle */}
              <Button
                variant="ghost"
                size="icon"
                className={`lg:hidden ${getTextColor()} hover:bg-transparent`}
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                data-testid="mobile-menu-toggle"
              >
                {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          </div>
        </nav>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence mode="wait">
        {isMobileMenuOpen && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-white dark:bg-emerald-950 pt-20 lg:hidden overflow-y-auto"
            data-testid="mobile-menu"
          >
            <nav className="container-custom py-8">
              {/* Main Links */}
              <div className="space-y-1">
                {navLinks.map((link, index) => (
                  <motion.div
                    key={link.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link
                      to={link.path}
                      className={`block py-4 text-lg font-serif border-b border-emerald-100 dark:border-emerald-800 transition-colors ${
                        isActive(link.path) 
                          ? 'text-emerald-700 dark:text-emerald-400' 
                          : 'text-emerald-900 dark:text-white'
                      }`}
                      data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.name}
                    </Link>
                  </motion.div>
                ))}
              </div>
              
              {/* Emerald AI Button */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="mt-8 pt-6 border-t border-emerald-200 dark:border-emerald-800"
              >
                <button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    setShowAIModal(true);
                  }}
                  className="flex items-center gap-3 py-3 px-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-200 w-full"
                  data-testid="mobile-nav-emerald-ai-btn"
                >
                  <Sparkles className="w-5 h-5" />
                  <div>
                    <span className="font-medium">Emerald AI</span>
                    <p className="text-xs text-emerald-600 dark:text-emerald-400">Chat & Trip Planner</p>
                  </div>
                </button>
              </motion.div>

              {/* Book Now CTA */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="mt-6"
              >
                <Link
                  to="/book"
                  className="flex items-center justify-center gap-2 w-full py-4 bg-emerald-800 hover:bg-emerald-700 text-white text-sm uppercase tracking-wider font-medium transition-all duration-300 rounded-sm"
                  data-testid="mobile-book-now-btn"
                >
                  <Phone className="h-4 w-4" />
                  Book Your Stay
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Emerald AI Modal */}
      <EmeraldAIModal open={showAIModal} onOpenChange={setShowAIModal} />
    </>
  );
}
